import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { GraduationCap, User, Phone, Mail, Calendar, Hash, BookOpen, Users, ArrowLeft, ArrowRight, Check, RefreshCw } from 'lucide-react';

const StudentRegistration = ({ onRegisterComplete }) => {
    const [step, setStep] = useState(1); // 1: Details, 2: OTP, 3: Password, 4: Success
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        roll_no: '',
        batch_id: '',
        pgm_id: '',
        admitted_date: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [otp, setOtp] = useState('');
    const [batches, setBatches] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [otpVerified, setOtpVerified] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [createdUserId, setCreatedUserId] = useState('');

    // Fetch batches and programs on mount
    useEffect(() => {
        fetchBatches();
        fetchPrograms();
    }, []);

    // Countdown for resend OTP
    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    const fetchBatches = async () => {
        try {
            const res = await axios.get('/api/batches');
            console.log('Batches response:', res.data);
            setBatches(res.data);
        } catch (err) {
            console.error('Error fetching batches:', err);
        }
    };

    const fetchPrograms = async () => {
        try {
            const res = await axios.get('/api/programs');
            console.log('Programs response:', res.data);
            setPrograms(res.data);
        } catch (err) {
            console.error('Error fetching programs:', err);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError('');
    };

    const getDateBounds = () => {
        const now = new Date();
        const today = now.toISOString().split('T')[0];
        const threeYearsAgo = new Date(now);
        threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);
        const minDate = threeYearsAgo.toISOString().split('T')[0];
        return { minDate, maxDate: today };
    };

    const validateStep1 = () => {
        if (!formData.name.trim()) return 'Full Name is required';
        if (!formData.phone.trim()) return 'Phone Number is required';
        if (!/^\d{10}$/.test(formData.phone.trim())) return 'Phone Number must be exactly 10 digits';
        if (!formData.roll_no.trim()) return 'Roll Number is required';
        if (!formData.batch_id) return 'Please select a Batch';
        if (!formData.pgm_id) return 'Please select a Program';
        if (!formData.admitted_date) return 'Date of Admission is required';
        const { minDate, maxDate } = getDateBounds();
        if (formData.admitted_date < minDate) return 'Date of Admission cannot be older than 3 years';
        if (formData.admitted_date > maxDate) return 'Date of Admission cannot be in the future';
        if (!formData.email.trim()) return 'Email Address is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Please enter a valid email address';
        return '';
    };

    const handleSendOTP = async () => {
        const validationError = validateStep1();
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);
        setError('');
        
        try {
            const res = await axios.post('/api/student/register/send-otp', {
                name: formData.name,
                phone: formData.phone,
                roll_no: formData.roll_no,
                batch_id: formData.batch_id,
                pgm_id: formData.pgm_id,
                admitted_date: formData.admitted_date,
                email: formData.email
            });
            
            setOtpSent(true);
            setSuccess(res.data.message);
            setCountdown(60);
            setStep(2);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to send OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOTP = async () => {
        if (!otp.trim()) {
            setError('Please enter the OTP');
            return;
        }
        if (otp.length !== 6) {
            setError('OTP must be 6 digits');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const res = await axios.post('/api/student/register/verify-otp', {
                email: formData.email,
                otp: otp
            });

            setOtpVerified(true);
            setSuccess(res.data.message);
            setStep(3);
        } catch (err) {
            setError(err.response?.data?.error || 'Invalid OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleResendOTP = async () => {
        if (countdown > 0) return;

        setLoading(true);
        setError('');

        try {
            const res = await axios.post('/api/student/register/resend-otp', {
                name: formData.name,
                phone: formData.phone,
                roll_no: formData.roll_no,
                batch_id: formData.batch_id,
                pgm_id: formData.pgm_id,
                admitted_date: formData.admitted_date,
                email: formData.email
            });

            setSuccess(res.data.message);
            setCountdown(60);
            setOtp('');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to resend OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleCompleteRegistration = async () => {
        if (!formData.password) {
            setError('Password is required');
            return;
        }
        if (formData.password.length < 4) {
            setError('Password must be at least 4 characters');
            return;
        }
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const res = await axios.post('/api/student/register/complete', {
                email: formData.email,
                password: formData.password
            });

            setSuccess(res.data.message);
            setCreatedUserId(res.data.user_id || '');
            setStep(4);
            
            // Redirect to login page after showing success screen
            setTimeout(() => {
                if (onRegisterComplete) {
                    onRegisterComplete(res.data);
                }
                window.location.href = '/';
            }, 3000);
        } catch (err) {
            setError(err.response?.data?.error || 'Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const goBack = () => {
        if (step > 1) {
            setStep(step - 1);
            setError('');
            setSuccess('');
        }
    };

    // Styles
    const styles = {
        page: {
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #1e3a8a 0%, #0f766e 100%)',
            padding: '20px',
            position: 'relative'
        },
        bgPattern: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'radial-gradient(circle at 25px 25px, rgba(255,255,255,0.1) 2px, transparent 0)',
            backgroundSize: '50px 50px',
            pointerEvents: 'none'
        },
        container: {
            background: '#fff',
            borderRadius: '24px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            width: '100%',
            maxWidth: '600px',
            overflow: 'hidden',
            position: 'relative'
        },
        header: {
            background: 'linear-gradient(145deg, rgba(30,58,138,0.1), rgba(15,118,110,0.1))',
            padding: '40px 40px 30px',
            textAlign: 'center'
        },
        logoContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            marginBottom: '15px'
        },
        logo: {
            width: '50px',
            height: '50px',
            background: 'linear-gradient(135deg, #1e3a8a, #0f766e)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        title: {
            fontSize: '28px',
            fontWeight: '700',
            color: '#1e3a8a',
            margin: 0
        },
        subtitle: {
            color: '#64748b',
            fontSize: '15px',
            marginTop: '8px'
        },
        progressBar: {
            display: 'flex',
            justifyContent: 'center',
            gap: '8px',
            marginTop: '25px'
        },
        progressStep: {
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            background: '#e2e8f0',
            transition: 'all 0.3s ease'
        },
        progressStepActive: {
            background: '#1e3a8a',
            transform: 'scale(1.2)'
        },
        progressStepCompleted: {
            background: '#10b981'
        },
        form: {
            padding: '30px 40px 40px'
        },
        inputGroup: {
            marginBottom: '20px',
            position: 'relative'
        },
        label: {
            display: 'block',
            marginBottom: '8px',
            fontWeight: '500',
            color: '#374151',
            fontSize: '14px'
        },
        input: {
            width: '100%',
            padding: '14px 16px',
            border: '2px solid #e2e8f0',
            borderRadius: '12px',
            fontSize: '15px',
            transition: 'all 0.3s ease',
            boxSizing: 'border-box'
        },
        inputSelect: {
            width: '100%',
            padding: '14px 16px',
            border: '2px solid #e2e8f0',
            borderRadius: '12px',
            fontSize: '15px',
            background: '#fff',
            cursor: 'pointer',
            boxSizing: 'border-box'
        },
        row: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '20px'
        },
        errorBox: {
            background: 'linear-gradient(90deg, #fee2e2, #fecaca)',
            color: '#dc2626',
            padding: '14px',
            borderRadius: '10px',
            borderLeft: '4px solid #ef4444',
            marginBottom: '20px',
            fontSize: '14px'
        },
        successBox: {
            background: 'linear-gradient(90deg, #d1fae5, #a7f3d0)',
            color: '#065f46',
            padding: '14px',
            borderRadius: '10px',
            borderLeft: '4px solid #10b981',
            marginBottom: '20px',
            fontSize: '14px'
        },
        button: {
            width: '100%',
            padding: '16px',
            background: 'linear-gradient(135deg, #1e3a8a 0%, #0f766e 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            marginTop: '10px'
        },
        buttonSecondary: {
            background: '#f1f5f9',
            color: '#475569',
            border: '2px solid #e2e8f0'
        },
        buttonDisabled: {
            opacity: 0.7,
            cursor: 'not-allowed'
        },
        linkButton: {
            background: 'none',
            border: 'none',
            color: '#1e3a8a',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            textDecoration: 'underline',
            padding: 0,
            marginTop: '15px',
            display: 'block',
            width: '100%',
            textAlign: 'center'
        },
        otpContainer: {
            display: 'flex',
            gap: '10px',
            justifyContent: 'center',
            marginBottom: '20px'
        },
        otpInput: {
            width: '50px',
            height: '60px',
            textAlign: 'center',
            fontSize: '24px',
            fontWeight: '600',
            border: '2px solid #e2e8f0',
            borderRadius: '12px'
        },
        successContainer: {
            textAlign: 'center',
            padding: '40px 20px'
        },
        successIcon: {
            width: '80px',
            height: '80px',
            background: '#d1fae5',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
        },
        backLink: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#64748b',
            fontSize: '14px',
            cursor: 'pointer',
            marginBottom: '20px'
        }
    };

    return (
        <div style={styles.page}>
            <div style={styles.bgPattern}></div>
            
            <div style={styles.container}>
                {/* Header */}
                <div style={styles.header}>
                    <div style={styles.logoContainer}>
                        <div style={styles.logo}>
                            <GraduationCap size={28} color="#fff" />
                        </div>
                        <h1 style={styles.title}>Student Registration</h1>
                    </div>
                    <p style={styles.subtitle}>Create your LMS account</p>
                    
                    {/* Progress Steps */}
                    <div style={styles.progressBar}>
                        {[1, 2, 3, 4].map((s) => (
                            <div
                                key={s}
                                style={{
                                    ...styles.progressStep,
                                    ...(step === s ? styles.progressStepActive : {}),
                                    ...(step > s ? styles.progressStepCompleted : {})
                                }}
                            />
                        ))}
                    </div>
                </div>

                {/* Form */}
                <div style={styles.form}>
                    {error && <div style={styles.errorBox}>{error}</div>}
                    {success && step !== 4 && <div style={styles.successBox}>{success}</div>}

                    {/* Step 1: Basic Details */}
                    {step === 1 && (
                        <>
                            <div style={styles.inputGroup}>
                                <label style={styles.label}>Full Name *</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="Enter your full name"
                                    style={styles.input}
                                />
                            </div>

                            <div style={styles.row}>
                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>Phone Number *</label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        placeholder="Enter phone number"
                                        maxLength={10}
                                        pattern="\d{10}"
                                        style={styles.input}
                                    />
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>Roll Number *</label>
                                    <input
                                        type="text"
                                        name="roll_no"
                                        value={formData.roll_no}
                                        onChange={handleChange}
                                        placeholder="Enter roll number"
                                        style={styles.input}
                                    />
                                </div>
                            </div>

                            <div style={styles.row}>
                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>Program *</label>
                                    <select
                                        name="pgm_id"
                                        value={formData.pgm_id}
                                        onChange={handleChange}
                                        style={styles.inputSelect}
                                    >
                                        <option value="">Select Program</option>
                                        {programs.map((pgm) => (
                                            <option key={pgm.pgm_id} value={pgm.pgm_id}>
                                                {pgm.pgm_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>Batch *</label>
                                    <select
                                        name="batch_id"
                                        value={formData.batch_id}
                                        onChange={handleChange}
                                        style={styles.inputSelect}
                                    >
                                        <option value="">Select Batch</option>
                                        {batches.map((batch) => (
                                            <option key={batch.batch_id} value={batch.batch_id}>
                                                {batch.batch_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div style={styles.row}>
                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>Date of Admission *</label>
                                    <input
                                        type="date"
                                        name="admitted_date"
                                        value={formData.admitted_date}
                                        onChange={handleChange}
                                        min={getDateBounds().minDate}
                                        max={getDateBounds().maxDate}
                                        style={styles.input}
                                    />
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>Email Address *</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="Enter email address"
                                        style={styles.input}
                                    />
                                </div>
                            </div>

                            <button
                                style={styles.button}
                                onClick={handleSendOTP}
                                disabled={loading}
                            >
                                {loading ? 'Sending OTP...' : 'Send OTP for Verification'}
                                {!loading && <ArrowRight size={18} />}
                            </button>
                        </>
                    )}

                    {/* Step 2: OTP Verification */}
                    {step === 2 && (
                        <>
                            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                                <Mail size={48} color="#1e3a8a" style={{ marginBottom: '15px' }} />
                                <h3 style={{ margin: '0 0 10px', color: '#1e293b' }}>Verify Your Email</h3>
                                <p style={{ color: '#64748b', margin: 0 }}>
                                    We've sent a 6-digit OTP to<br />
                                    <strong>{formData.email}</strong>
                                </p>
                            </div>

                            <div style={styles.otpContainer}>
                                {[0, 1, 2, 3, 4, 5].map((i) => (
                                    <input
                                        key={i}
                                        type="text"
                                        maxLength={1}
                                        value={otp[i] || ''}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, '');
                                            if (val) {
                                                const newOtp = otp.split('');
                                                newOtp[i] = val;
                                                setOtp(newOtp.join(''));
                                                // Focus next input
                                                if (i < 5) {
                                                    const inputs = document.querySelectorAll('.otp-input');
                                                    inputs[i + 1]?.focus();
                                                }
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Backspace' && !otp[i] && i > 0) {
                                                const inputs = document.querySelectorAll('.otp-input');
                                                inputs[i - 1]?.focus();
                                            }
                                        }}
                                        className="otp-input"
                                        style={styles.otpInput}
                                    />
                                ))}
                            </div>

                            <button
                                style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
                                onClick={handleVerifyOTP}
                                disabled={loading || otp.length !== 6}
                            >
                                {loading ? 'Verifying...' : 'Verify OTP'}
                            </button>

                            <button
                                style={styles.linkButton}
                                onClick={handleResendOTP}
                                disabled={countdown > 0 || loading}
                            >
                                {countdown > 0 ? (
                                    <>Resend OTP in {countdown}s</>
                                ) : (
                                    <><RefreshCw size={14} style={{ marginRight: '5px' }} /> Resend OTP</>
                                )}
                            </button>

                            <div style={styles.backLink} onClick={goBack}>
                                <ArrowLeft size={16} /> Back to details
                            </div>
                        </>
                    )}

                    {/* Step 3: Set Password */}
                    {step === 3 && (
                        <>
                            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                                <Check size={48} color="#10b981" style={{ marginBottom: '15px' }} />
                                <h3 style={{ margin: '0 0 10px', color: '#1e293b' }}>Email Verified!</h3>
                                <p style={{ color: '#64748b', margin: 0 }}>
                                    Welcome, <strong>{formData.name}</strong>!<br />
                                    Now set your password to complete registration.
                                </p>
                            </div>

                            <div style={styles.inputGroup}>
                                <label style={styles.label}>Password *</label>
                                <input
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder="Create a password (min 4 characters)"
                                    style={styles.input}
                                />
                            </div>

                            <div style={styles.inputGroup}>
                                <label style={styles.label}>Confirm Password *</label>
                                <input
                                    type="password"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    placeholder="Confirm your password"
                                    style={styles.input}
                                />
                            </div>

                            <button
                                style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
                                onClick={handleCompleteRegistration}
                                disabled={loading}
                            >
                                {loading ? 'Completing Registration...' : 'Complete Registration'}
                            </button>

                            <div style={styles.backLink} onClick={goBack}>
                                <ArrowLeft size={16} /> Back to OTP verification
                            </div>
                        </>
                    )}

                    {/* Step 4: Success */}
                    {step === 4 && (
                        <div style={styles.successContainer}>
                            <div style={styles.successIcon}>
                                <Check size={40} color="#10b981" />
                            </div>
                            <h2 style={{ color: '#1e293b', marginBottom: '15px' }}>Registration Successful!</h2>
                            <p style={{ color: '#64748b', marginBottom: '25px' }}>
                                Your account has been created successfully.<br />
                                You can now login to the LMS portal.
                            </p>
                            <div style={{
                                background: '#f1f5f9',
                                padding: '20px',
                                borderRadius: '12px',
                                textAlign: 'left',
                                marginBottom: '20px'
                            }}>
                                <p style={{ margin: '0 0 10px', color: '#475569', fontSize: '14px' }}>
                                    <strong>Login Credentials:</strong>
                                </p>
                                <p style={{ margin: '0', color: '#1e293b' }}>
                                    <strong>User ID:</strong> {createdUserId || 'Not available'}
                                </p>
                            </div>
                            <p style={{ color: '#64748b', fontSize: '14px' }}>
                                Redirecting to login page...
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default StudentRegistration;
